/**
 * ADR-211 / ADR-218 — the cart wire (`variantId:qty,…`) and the pure rules around it.
 *
 * One definition for `/cart?reorder=` (eros-cart-reorder.js) and `?add=` on any page
 * (eros-cart-add-link.js). Same rules as the worker (src/reorder/reorder_rules.ts) and the
 * app (lib/features/reorder/domain/reorder_lines.dart); all run design/reorder/reorder_cases.json
 * (scripts/tests/eros-cart-reorder.test.mjs). No DOM, no network.
 */
(function (root) {
  'use strict';

  var MAX_LINES = 50;
  var MAX_QTY = 6; /* the theme's per-line cap (eros-cart-line-qty-cap.js CAP) */

  function variantIdOrNull(raw) {
    var v = String(raw == null ? '' : raw).trim();
    if (!/^\d{1,20}$/.test(v)) return null;
    v = v.replace(/^0+/, '');
    return v.length ? v : null;
  }

  /** Lenient parse: bad items are skipped, repeats merged, ≤ 50 lines, qty ≤ 6. */
  function parseWire(raw) {
    var order = [];
    var qty = {};
    var parts = String(raw == null ? '' : raw).split(',');
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i].trim()) continue;
      var pair = parts[i].split(':');
      var id = variantIdOrNull(pair[0]);
      if (!id) continue;
      var q = pair.length < 2 ? '1' : pair[1].trim();
      if (!/^\d+$/.test(q)) continue;
      var n = Number(q);
      if (n < 1) continue;
      if (!Object.prototype.hasOwnProperty.call(qty, id)) {
        if (order.length >= MAX_LINES) continue;
        order.push(id);
        qty[id] = 0;
      }
      qty[id] = Math.min(MAX_QTY, qty[id] + n);
    }
    return order.map(function (id) {
      return { variantId: id, quantity: qty[id] };
    });
  }

  function formatWire(lines) {
    return lines
      .map(function (l) {
        return l.variantId + ':' + l.quantity;
      })
      .join(',');
  }

  /**
   * The `?<param>=` landing of a URL: the lines to add and the address without the param.
   * `search` is location.search; null when the param is absent.
   */
  function takeParam(search, param, path, hash) {
    var params = new URLSearchParams(search);
    if (!params.has(param)) return null;
    var lines = parseWire(params.get(param));
    params.delete(param);
    var rest = params.toString();
    return { lines: lines, target: path + (rest ? '?' + rest : '') + (hash || '') };
  }

  /** /cart.js items above the per-line cap (a link merged with lines already there). */
  function overCap(items) {
    return (items || [])
      .filter(function (it) {
        return it.quantity > MAX_QTY;
      })
      .map(function (it) {
        return { key: it.key, quantity: MAX_QTY };
      });
  }

  /**
   * ADR-222: the shopper's cart as the ADR-218 `?add=` wire, first 50 products, qty ≤ 6.
   * `lines` are {v: variant id or gid, q: quantity} in cart order. Shared with the app
   * through design/reorder/cart_share_cases.json.
   */
  function cartShareWire(lines) {
    var order = [];
    var qty = {};
    var truncated = false;
    (lines || []).forEach(function (l) {
      var m = /^(?:gid:\/\/shopify\/ProductVariant\/)?(\d{1,20})$/.exec(String(l && l.v != null ? l.v : '').trim());
      var n = Number(l && l.q);
      if (!m || !(n >= 1)) return;
      var id = m[1];
      if (!Object.prototype.hasOwnProperty.call(qty, id)) {
        if (order.length >= MAX_LINES) {
          truncated = true;
          return;
        }
        order.push(id);
        qty[id] = 0;
      }
      qty[id] = Math.min(MAX_QTY, qty[id] + n);
    });
    var wire = formatWire(order.map(function (id) { return { variantId: id, quantity: qty[id] }; }));
    return { wire: wire, truncated: truncated };
  }

  var api = {
    cartShareWire: cartShareWire,
    parseWire: parseWire,
    formatWire: formatWire,
    takeParam: takeParam,
    overCap: overCap,
    MAX_LINES: MAX_LINES,
    MAX_QTY: MAX_QTY
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.ErosReorderWire = api;
})(typeof window !== 'undefined' ? window : globalThis);
